import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import { downloadCSV as downloadGenericCSV } from '../../lib/functions';
import Button from '../Button';
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const TimeSheetReport = ({ timesheetData = [], theme, onUpdateComments }) => {
    const { profile, isAdmin, permissions, isDev } = useAuthProvider();
    const { showAlert } = useAlert();
    const canEdit = isDev || (permissions && permissions.perm_timesheets_edit);

    const [tableData, setTableData] = useState([]);
    const [editingComments, setEditingComments] = useState({});
    const [tempComments, setTempComments] = useState({});
    const [selectedRow, setSelectedRow] = useState(null);

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'driver_name', label: 'Driver Name' },
        { key: 'sun_start', label: 'Sun Start' },
        { key: 'sun_end', label: 'Sun End' },
        { key: 'mon_start', label: 'Mon Start' },
        { key: 'mon_end', label: 'Mon End' },
        { key: 'tue_start', label: 'Tue Start' },
        { key: 'tue_end', label: 'Tue End' },
        { key: 'wed_start', label: 'Wed Start' },
        { key: 'wed_end', label: 'Wed End' },
        { key: 'thu_start', label: 'Thu Start' },
        { key: 'thu_end', label: 'Thu End' },
        { key: 'fri_start', label: 'Fri Start' },
        { key: 'fri_end', label: 'Fri End' },
        { key: 'sat_start', label: 'Sat Start' },
        { key: 'sat_end', label: 'Sat End' },
        { key: 'total_hours', label: 'Total Hours' },
        { key: 'over_time', label: 'Over Time' },
        { key: 'holiday_hours', label: 'Holiday Hours' },
        { key: 'nights_out', label: 'Nights Out' },
        { key: 'used_moffett', label: 'Used Moffett' },
        { key: 'driver_comments', label: 'Driver Comments' },
        { key: 'comments', label: 'Comments' },
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);
    const [allowDownload, setAllowDownload] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true); // Open password modal on component mount
    }, []);

    const handlePasswordSubmission = async (password) => {
        const email = profile.email;

        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
                setAllowDownload(true); // Allow download if password is valid
                setOpenRequestPassword(false);
            } else {
                logger.log("Invalid password.");
                setTableData([]); // Clear data if password is invalid
                showAlert({
                    message: 'Invalid password. Access denied.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
                setOpenRequestPassword(false);
            }
        } catch (error) {
            logger.error("Error validating password:", error);
            setTableData([]);
            showAlert({
                message: 'An error occurred while validating the password.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
            setOpenRequestPassword(false);
        }
    };

    const handlePasswordModalClose = () => {
        setOpenRequestPassword(false);
        // When user closes password modal without entering password, go back to dashboard
        // You might want to add navigation logic here or emit an event to parent
    };

    const sortByColumn = (column) => {
        const sorted = [...tableData].sort((a, b) => {
            const aVal = a[column]?.toString().toLowerCase() || '';
            const bVal = b[column]?.toString().toLowerCase() || '';
            return aVal.localeCompare(bVal);
        });
        setTableData(sorted);
    };

    const handleRowClick = (rowId) => {
        setSelectedRow(selectedRow === rowId ? null : rowId);
    };

    const showData = () => {
        if (timesheetData && timesheetData.length > 0) {
            setTableData(timesheetData);
        }
    };

    // Comment editing functions
    const startEditingComments = (timesheetId, currentComments) => {
        setEditingComments({ ...editingComments, [timesheetId]: true });
        setTempComments({ ...tempComments, [timesheetId]: currentComments || '' });
    };

    const cancelEditingComments = (timesheetId) => {
        const newEditingComments = { ...editingComments };
        delete newEditingComments[timesheetId];
        setEditingComments(newEditingComments);

        const newTempComments = { ...tempComments };
        delete newTempComments[timesheetId];
        setTempComments(newTempComments);
    };

    const saveComments = async (timesheetId) => {
        const newComments = tempComments[timesheetId] || '';
        
        try {
            await onUpdateComments(timesheetId, newComments);
            
            // Update the local data
            const updatedData = tableData.map(item => 
                item.id === timesheetId 
                    ? { ...item, comments: newComments }
                    : item
            );
            setTableData(updatedData);

            // Clear editing state
            cancelEditingComments(timesheetId);

            showAlert({
                message: 'Comments updated successfully',
                type: 'success',
                buttons: [{
                    type: 'success',
                    label: 'OK',
                }],
            });
        } catch (error) {
            showAlert({
                message: 'Failed to update comments',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'OK',
                }],
            });
        }
    };

    const formatTimeDisplay = (timeStr) => {
        return timeStr && timeStr !== '' ? timeStr : '-';
    };

    const formatNumberDisplay = (num) => {
        return num != null ? num.toString() : '0';
    };

    // Helper function to format date as dd/mm/yyyy
    const formatDateDisplay = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // CSV Download function
    const downloadCSV = () => {
        if (!allowDownload) {
            showAlert({
                message: 'Please validate your password first.',
                type: 'warning',
                buttons: [{
                    type: 'warning',
                    label: 'OK',
                }],
            });
            return;
        }

        if (tableData.length === 0) {
            showAlert({
                message: 'No data available to download.',
                type: 'info',
                buttons: [{
                    type: 'info',
                    label: 'OK',
                }],
            });
            return;
        }

        // Format data for CSV with time prefixes to prevent Excel auto-formatting
        const csvData = tableData.map(row => ({
            'Driver Name': row.driver_name,
            'Group': row.group,
            'Week Commencing': row.week_commencing,
            'Sunday Start': row.sun_start ? `'${row.sun_start}` : '',
            'Sunday End': row.sun_end ? `'${row.sun_end}` : '',
            'Monday Start': row.mon_start ? `'${row.mon_start}` : '',
            'Monday End': row.mon_end ? `'${row.mon_end}` : '',
            'Tuesday Start': row.tue_start ? `'${row.tue_start}` : '',
            'Tuesday End': row.tue_end ? `'${row.tue_end}` : '',
            'Wednesday Start': row.wed_start ? `'${row.wed_start}` : '',
            'Wednesday End': row.wed_end ? `'${row.wed_end}` : '',
            'Thursday Start': row.thu_start ? `'${row.thu_start}` : '',
            'Thursday End': row.thu_end ? `'${row.thu_end}` : '',
            'Friday Start': row.fri_start ? `'${row.fri_start}` : '',
            'Friday End': row.fri_end ? `'${row.fri_end}` : '',
            'Saturday Start': row.sat_start ? `'${row.sat_start}` : '',
            'Saturday End': row.sat_end ? `'${row.sat_end}` : '',
            'Total Hours': row.total_hours,
            'Over Time': row.over_time,
            'Holiday Hours': row.holiday_hours,
            'Nights Out': row.nights_out,
            'Used Moffett': row.used_moffett,
            'Comments': row.comments || '',
            'Driver Comments': row.driver_comments || ''  // Add driver comments to CSV
        }));

        const weekCommencing = tableData[0]?.week_commencing || new Date().toISOString().split('T')[0];
        const filename = `timesheet_report_${weekCommencing.replace(/-/g, '')}`;

        downloadGenericCSV(csvData, filename);

        showAlert({
            message: 'Time Sheet Report downloaded successfully!',
            type: 'success',
            buttons: [{
                type: 'success',
                label: 'OK',
            }],
        });
    };

    // Styles
    const containerStyle = {
        padding: '20px',
        backgroundColor: isLightTheme ? '#fff' : '#1a1a1a',
        color: isLightTheme ? '#000' : '#fff',
        borderRadius: '10px',
        boxShadow: isLightTheme ? '0 2px 10px rgba(0,0,0,0.1)' : '0 2px 10px rgba(255,255,255,0.1)',
    };

    const headerStyle = {
        backgroundColor: isLightTheme ? '#f8f9fa' : '#2c2c2c',
        color: isLightTheme ? '#333' : '#fff',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px'
    };

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        backgroundColor: isLightTheme ? '#fff' : '#1a1a1a',
        border: `1px solid ${isLightTheme ? '#ddd' : '#444'}`,
        borderRadius: '8px',
        overflow: 'hidden',
    };

    const thStyle = {
        backgroundColor: isLightTheme ? '#007bff' : '#0056b3',
        color: '#fff',
        padding: '12px 8px',
        textAlign: 'left',
        fontWeight: 'bold',
        borderBottom: `2px solid ${isLightTheme ? '#0056b3' : '#007bff'}`,
        cursor: 'pointer',
        fontSize: '14px',
        minWidth: '80px'
    };

    const tdStyle = {
        padding: '10px 8px',
        borderBottom: `1px solid ${isLightTheme ? '#eee' : '#333'}`,
        backgroundColor: isLightTheme ? '#fff' : '#1a1a1a',
        color: isLightTheme ? '#333' : '#fff',
        fontSize: '14px',
    };

    const inputStyle = {
        width: '100%',
        padding: '5px',
        border: `1px solid ${isLightTheme ? '#ccc' : '#555'}`,
        borderRadius: '4px',
        backgroundColor: isLightTheme ? '#fff' : '#333',
        color: isLightTheme ? '#000' : '#fff',
        fontSize: '14px',
    };

    const buttonGroupStyle = {
        display: 'flex',
        gap: '5px',
        alignItems: 'center',
    };

    if (openRequestPassword) {
        return (
            <RequestPassword
                isOpen={openRequestPassword}
                setIsOpen={handlePasswordModalClose}
                returnData={handlePasswordSubmission}
                theme={theme}
            />
        );
    }

    return (
        <div className="container-fluid p-0">
            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`} style={{ padding: '20px' }}>
                    <div style={{ textAlign: 'left' }}>
                        <h4 className="mb-2" style={{ fontWeight: '600', letterSpacing: '0.5px' }}>Time Sheet Report</h4>
                        {tableData.length > 0 && (
                            <div style={{ 
                                fontSize: '16px', 
                                color: isLightTheme ? '#666' : '#ccc',
                                fontWeight: '500',
                                lineHeight: '1.5'
                            }}>
                                <div>Week Commencing: <strong>{formatDateDisplay(tableData[0]?.week_commencing)}</strong></div>
                                <div style={{ marginTop: '4px' }}>{tableData.length} drivers</div>
                            </div>
                        )}
                    </div>
                    
                    {canEdit && (
                        <div style={{ marginTop: '15px', width: '100%' }}>
                            <Button
                                variant="success"
                                onClick={downloadCSV}
                                disabled={!allowDownload || tableData.length === 0}
                                style={{ width: '100%', padding: '10px' }}
                            >
                                Download CSV
                            </Button>
                        </div>
                    )}
                </div>

                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'} p-0`}>

                    {tableData.length > 0 ? (
                        <div className="table-responsive" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                            <table className={`table table-striped table-hover mb-0 ${isLightTheme ? 'table-light' : 'table-dark'}`}>
                                <thead className={isLightTheme ? 'table-dark' : 'table-light'}>
                                    <tr>
                                        {displayColumns.map((column) => (
                                            <th
                                                key={column.key}
                                                onClick={() => sortByColumn(column.key)}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    minWidth: column.key === 'driver_name' ? '150px' : 
                                                             column.key === 'comments' ? '200px' : 
                                                             column.key === 'driver_comments' ? '200px' : '80px',
                                                    width: column.key === 'driver_name' ? '180px' : 
                                                           column.key === 'comments' ? '300px' : 
                                                           column.key === 'driver_comments' ? '300px' : 'auto',
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: column.key === 'driver_name' ? 3 : 2,
                                                    left: column.key === 'driver_name' ? 0 : 'auto',
                                                    backgroundColor: isLightTheme ? '#212529' : '#f8f9fa',
                                                    color: isLightTheme ? '#fff' : '#000'
                                                }}
                                                title={`Sort by ${column.label}`}
                                            >
                                                {column.label}
                                            </th>
                                        ))}
                                        {isAdmin && canEdit && <th style={{ fontSize: '14px', minWidth: '100px', position: 'sticky', top: 0, zIndex: 2, backgroundColor: isLightTheme ? '#212529' : '#f8f9fa', color: isLightTheme ? '#fff' : '#000' }}>Actions</th>}
                                    </tr>
                        </thead>
                                <tbody>
                                    {tableData.map((row, index) => (
                                        <tr 
                                            key={row.id || index}
                                            style={{
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handleRowClick(row.id)}
                                        >
                                            <td 
                                                style={{ 
                                                    fontSize: '14px', 
                                                    verticalAlign: 'middle', 
                                                    minWidth: '150px', 
                                                    width: '180px',
                                                    fontWeight: selectedRow === row.id ? 'bold' : 'normal',
                                                    color: selectedRow === row.id 
                                                        ? (isLightTheme ? '#2e7d32' : '#81c784')  // Dark green in light mode, light green in dark mode
                                                        : 'inherit',
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 1,
                                                    backgroundColor: isLightTheme ? '#fff' : '#212529'
                                                }}
                                            >
                                                {row.driver_name}
                                            </td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.sun_start)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.sun_end)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.mon_start)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.mon_end)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.tue_start)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.tue_end)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.wed_start)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.wed_end)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.thu_start)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.thu_end)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.fri_start)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.fri_end)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.sat_start)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatTimeDisplay(row.sat_end)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatNumberDisplay(row.total_hours)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatNumberDisplay(row.over_time)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatNumberDisplay(row.holiday_hours)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatNumberDisplay(row.nights_out)}</td>
                                            <td style={{ fontSize: '14px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>{formatNumberDisplay(row.used_moffett)}</td>
                                            <td style={{ fontSize: '14px', minWidth: '200px', width: '300px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>
                                                <div style={{ wordWrap: 'break-word', maxWidth: '300px' }}>
                                                    {row.driver_comments || '-'}
                                                </div>
                                            </td>
                                            <td style={{ fontSize: '14px', minWidth: '200px', width: '300px', verticalAlign: 'middle', fontWeight: selectedRow === row.id ? 'bold' : 'normal', color: selectedRow === row.id ? (isLightTheme ? '#2e7d32' : '#81c784') : 'inherit' }}>
                                                {editingComments[row.id] ? (
                                                    <textarea
                                                        className={`form-control form-control-sm ${isLightTheme ? '' : 'bg-dark text-light border-secondary'}`}
                                                        style={{ minHeight: '60px', fontSize: '14px' }}
                                                        value={tempComments[row.id] || ''}
                                                        onChange={(e) => setTempComments({
                                                            ...tempComments,
                                                            [row.id]: e.target.value
                                                        })}
                                                        placeholder="Enter comments..."
                                                    />
                                                ) : (
                                                    <div style={{ wordWrap: 'break-word', maxWidth: '300px' }}>
                                                        {row.comments || '-'}
                                                    </div>
                                                )}
                                            </td>
                                            {isAdmin && canEdit && (
                                                <td style={{ fontSize: '14px', verticalAlign: 'middle' }}>
                                                    <div className="btn-group btn-group-sm"
                                                         style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {editingComments[row.id] ? (
                                                    <>
                                                        <button
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => saveComments(row.id)}
                                                            style={{ 
                                                                padding: '1px 4px', 
                                                                fontSize: '10px', 
                                                                lineHeight: '1.1',
                                                                height: '20px',
                                                                minWidth: '35px',
                                                                marginRight: '2px'
                                                            }}
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={() => cancelEditingComments(row.id)}
                                                            style={{ 
                                                                padding: '1px 4px', 
                                                                fontSize: '10px', 
                                                                lineHeight: '1.1',
                                                                height: '20px',
                                                                minWidth: '40px'
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => startEditingComments(row.id, row.comments)}
                                                        style={{ 
                                                            padding: '1px 4px', 
                                                            fontSize: '10px', 
                                                            lineHeight: '1.1',
                                                            height: '20px',
                                                            minWidth: '30px'
                                                        }}
                                                    >
                                                        + Comment
                                                    </button>
                                                )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center p-4">
                            <h5>No timesheet data available for the selected date.</h5>
                            <p className="text-muted">Please select a different date or ensure data has been generated.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TimeSheetReport;